import { Button, ChipList, ChipListChangeEvent } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { DateTimePicker, DateTimePickerChangeEvent, TimePicker, TimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
    Checkbox,
    CheckboxChangeEvent,
    ColorPickerChangeEvent,
    NumericTextBox,
    NumericTextBoxChangeEvent,
    Slider,
    SliderChangeEvent,
    Switch,
    SwitchChangeEvent,
    TextBox,
    TextBoxChangeEvent
} from '@progress/kendo-react-inputs';
import { clipboardCodeIcon, copyIcon } from '@progress/kendo-svg-icons';
import * as turf from '@turf/turf';
import dayjs from 'dayjs';
import React from 'react';
import { ColorPickerWithDefault } from '../../app/components/inputs/ColorPickerWithDefault';
import WorkspaceItemContext from '../../app/contexts/workspace/workspace-item-context';
import { useAppDispatch } from '../../app/hooks/store-hooks';
import useMap from '../../app/hooks/use-map';
import { createDrawingObject, updateDrawingObject } from '../../app/state/slices/drawing-objects-slice';
import { drawingObjectSetIdleWorkspace } from '../../app/state/slices/workspace-slice';
import { patchFocusedWorkspaceView, store } from '../../app/state/store';
import { FPMSyncType } from '../../common/enums';
import { formatLatLngNotam, parsePositions } from '../../common/position-parser';
import { DateTimeFormatter } from '../../formatters';
import useDebugFeatures from '../../hooks/use-debug-features';
import useIntegration from '../../hooks/use-integration';
import useUserRights from '../../hooks/use-user-rights';
import { AddOutline, EditOutline, UserGroupOutline, UserOutline } from '../../icons/icons';
import LatLngInput from '../common/LatLngInput';
import { DrawingObjectCentroidProperties, DrawingObjectProperties } from './drawing-object-layer';
import DrawingObjectPolygonPoints, { PolygonPoints } from './DrawingObjectPolygonPoints';
import {
    DrawingObject,
    DrawingObjectCategory,
    DrawingObjectCenterStyle,
    DrawingObjectCircleGeometry,
    DrawingObjectFillStyle,
    DrawingObjectGeometry,
    DrawingObjectLabelAlignment,
    DrawingObjectLineType,
    DrawingObjectOffsetUnits,
    DrawingObjectPolygonGeometry,
    DrawingObjectRectangleGeometry,
    DrawingObjectType
} from './types';

type DropdownItem<T> = { text: string; value: T };
export interface DrawingObjectEditProps {
    object: DrawingObject;
}

// fpm fill color constant
export const FPMFillColor = '#ff0000';
export const FPMLineColor = '#ff0000';

const DRAWING_OBJECT_FILL_DEFAULT_COLOR = '#ff00ff80';
const DRAWING_OBJECT_LINE_DEFAULT_COLOR = '#00000080';
const DRAWING_OBJECT_CENTER_DEFAULT_COLOR = '#ffffff';
const DRAWING_OBJECT_LABEL_DEFAULT_COLOR = '#000000';

const DrawingObjectEditWindow = (props: DrawingObjectEditProps) => {
    const map = useMap();
    const context = React.useContext(WorkspaceItemContext);
    const layer = context.drawingObjectsLayer;
    const idSource = React.useRef(0);
    const nextId = React.useCallback(() => idSource.current++, []);
    const [busy, setBusy] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const nameFieldRef = React.useRef<HTMLDivElement>(null);
    const rights = useUserRights();
    const integrationInfo = useIntegration();
    const dispatch = useAppDispatch();
    const debug = useDebugFeatures();

    const [objectName, setObjectName] = React.useState<string>('');
    const [objectType, setObjectType] = React.useState(ObjectType[1]);
    const [label, setLabel] = React.useState<string>('');
    const [labelAlignment, setLabelAlignment] = React.useState(val(LabelAlignmentOption, DrawingObjectLabelAlignment.perimeter));
    const [labelColor, setLabelColor] = React.useState('');

    const [lineStyle, setLineStyle] = React.useState(LineStyleOptions[0]);
    const [lineColor, setLineColor] = React.useState<string>('#fff');
    const [lineWidth, setLineWidth] = React.useState<number>(1);

    const [fillStyle, setFillStyle] = React.useState(FillStyleOptions[0]);
    const [fillColor, setFillColor] = React.useState<string>('#fff');

    const [centerPointStyle, setCenterPointStyle] = React.useState(CenterStyleOptions[0]);
    const [centerPointColor, setCenterPointColor] = React.useState('');
    const [centerPointSize, setCenterPointSize] = React.useState(0);

    const [objectCategory, setObjectCategory] = React.useState(ObjectCategoryOptions[0]);

    const [objectZoom, setObjectZoom] = React.useState(0);
    const [pointZoom, setPointZoom] = React.useState(0);
    const [labelZoom, setLabelZoom] = React.useState(0);

    const [altitudeMin, setAltitudeMin] = React.useState<number | null>(null);
    const [altitudeMax, setAltitudeMax] = React.useState<number | null>(null);

    const [alertPlane, setAlertPlane] = React.useState(false);
    const [alertRoute, setAlertRoute] = React.useState(false);

    const [syncFPM, setSyncFPM] = React.useState(props.object.fpmProperties?.sync ?? false);
    const [objectFPMSyncType, setObjectFPMSyncType] = React.useState(props.object.fpmProperties?.FPMSyncType ?? FPMSyncType.global);

    const [validTimeStart, setValidTimeStart] = React.useState<Date | null>(
        dayjs(props.object.validTime?.startDay ?? undefined)
            .utc()
            .toDate()
    );
    const [validTimeStartEnabled, setValidTimeStartEnabled] = React.useState<boolean>(props.object.validTime?.startDay != null);
    const [validTimeEnd, setValidTimeEnd] = React.useState<Date | null>(
        props.object.validTime?.endDay ? dayjs(props.object.validTime?.endDay).utc().toDate() : dayjs().utc().add(8, 'hour').toDate()
    );
    const [validTimeEndEnabled, setValidTimeEndEnabled] = React.useState<boolean>(props.object.validTime?.endDay != null);
    const [dailyStartTime, setDailyStartTime] = React.useState<Date | null>(() => {
        let [hours, minutes, seconds] = [0, 0, 0];
        if (props.object.validTime?.startTime) {
            [hours, minutes, seconds] = props.object.validTime.startTime.split(':').map(Number);
        }
        return dayjs().utc().hour(hours).minute(minutes).second(seconds).toDate();
    });
    const [dailyStartTimeEnabled, setDailyStartTimeEnabled] = React.useState<boolean>(props.object.validTime?.startTime != null);
    const [dailyEndTime, setDailyEndTime] = React.useState<Date | null>(() => {
        let [hours, minutes, seconds] = [23, 59, 59];
        if (props.object.validTime?.endTime) {
            [hours, minutes, seconds] = props.object.validTime.endTime.split(':').map(Number);
        }
        return dayjs().utc().hour(hours).minute(minutes).second(seconds).toDate();
    });
    const [dailyEndTimeEnabled, setDailyEndTimeEnabled] = React.useState<boolean>(props.object.validTime?.endTime != null);
    const [validDaysOfWeek, setValidDaysOfWeek] = React.useState<number>(props.object.validTime?.activeDaysOfWeek ?? 0b1111111);
    const [grayOut, setGrayOut] = React.useState<boolean>(props.object.validTime?.greyOutNonActive ?? false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [centerUnits, setCenterUnits] = React.useState(0);
    const [centerX, setCenterX] = React.useState(0);
    const [centerY, setCenterY] = React.useState(0);

    const [offsetUnits, setOffsetUnits] = React.useState(OffsetUnitsOptions[0]);
    const [offsetRadiusX, setOffsetRadiusX] = React.useState(0);
    const [offsetRadiusY, setOffsetRadiusY] = React.useState(0);

    const [polygonPoints, setPolygonPoints] = React.useState<PolygonPoints[]>([]);
    const [pasteError, setPasteError] = React.useState(false);

    const isNew = React.useMemo(() => !store.getState().drawingObjects.objects.find((x) => x.id === props.object.id), [props.object]);

    const updateGeometryProperties = React.useCallback((type: DrawingObjectType, geometry: DrawingObjectGeometry) => {
        const updateRectangle = (geometry: DrawingObjectRectangleGeometry) => {
            setCenterX(geometry.centerX);
            setCenterY(geometry.centerY);
            setOffsetUnits(val(OffsetUnitsOptions, geometry.sizeUnits));
            setOffsetRadiusX(geometry.width);
            setOffsetRadiusY(geometry.height);
        };
        const updateCircle = (geometry: DrawingObjectCircleGeometry) => {
            setCenterX(geometry.centerX);
            setCenterY(geometry.centerY);
            setOffsetUnits(val(OffsetUnitsOptions, geometry.radiusUnits));
            setOffsetRadiusX(geometry.radiusX);
            setOffsetRadiusY(geometry.radiusY);
        };
        const updatePolygon = (geometry: DrawingObjectPolygonGeometry) => {
            const center = turf.centroid({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [geometry.polygonCoordinates]
                },
                properties: {}
            });

            setCenterX(center.geometry.coordinates[0]);
            setCenterY(center.geometry.coordinates[1]);

            setPolygonPoints(
                geometry.polygonCoordinates.map((x) => ({
                    id: nextId(),
                    lngLat: x
                }))
            );
        };

        switch (type) {
            case DrawingObjectType.circle:
                updateCircle(geometry as DrawingObjectCircleGeometry);
                break;
            case DrawingObjectType.polygon:
                updatePolygon(geometry as DrawingObjectPolygonGeometry);
                break;
            case DrawingObjectType.rectangle:
                updateRectangle(geometry as DrawingObjectRectangleGeometry);
                break;
        }
    }, []);

    React.useEffect(() => {
        const object = props.object;
        setObjectName(object.name);
        setObjectType(val(ObjectType, object.isCompany));
        setLabel(object.label.text);
        setLabelAlignment(val(LabelAlignmentOption, object.label.alignment));
        setLabelColor(object.label.color);
        setLineStyle(val(LineStyleOptions, object.line.style));
        setLineColor(object.line.color);
        setLineWidth(object.line.width);
        setFillStyle(val(FillStyleOptions, object.fill.style));
        setFillColor(object.fill.color);
        setCenterPointStyle(val(CenterStyleOptions, object.center.style));
        setCenterPointColor(object.center.color);
        setCenterPointSize(object.center.size);
        setObjectCategory(val(ObjectCategoryOptions, object.category));
        setObjectZoom(object.zoom.object);
        setPointZoom(object.zoom.point);
        setLabelZoom(object.zoom.label);
        setAltitudeMin(object.altitude.min);
        setAltitudeMax(object.altitude.max);
        setAlertPlane(object.alerts.plane);
        setAlertRoute(object.alerts.route);
        updateSyncFPM({ value: object.fpmProperties?.sync ?? false } as CheckboxChangeEvent);
        updateGeometryProperties(object.type, object.geometry);
    }, [props.object]);

    const [isEditing, setIsEditing] = React.useState(false);

    const updatePropertyObject = React.useCallback(
        <T extends keyof DrawingObjectProperties>(key: T, value: DrawingObjectProperties[T]) =>
            layer.updatePropertyObject(props.object.id, key, value),
        [layer, props.object]
    );
    const updatePropertyCentroid = React.useCallback(
        <T extends keyof DrawingObjectCentroidProperties>(key: T, value: DrawingObjectCentroidProperties[T]) =>
            layer.updatePropertyCenter(props.object.id, key, value),
        [layer, props.object]
    );

    const updateObjectName = React.useCallback((e: TextBoxChangeEvent) => {
        setObjectName(e.value as string);
        setErrorMessage('');
    }, []);

    const updateObjectType = React.useCallback((e: DropDownListChangeEvent) => setObjectType(e.value), []);

    const updateLabel = React.useCallback(
        (e: TextBoxChangeEvent) => {
            setLabel(e.value as string);
            updatePropertyCentroid('label', e.value as string);
            updatePropertyObject('label', e.value as string);
        },
        [updatePropertyCentroid, updatePropertyObject]
    );

    const updateLabelColor = React.useCallback(
        (e: ColorPickerChangeEvent) => {
            setLabelColor(e.value);
            updatePropertyCentroid('labelColor', e.value);
            updatePropertyObject('labelColor', e.value);
        },
        [updatePropertyCentroid, updatePropertyObject]
    );

    const updateLabelAlignment = React.useCallback(
        (e: DropDownListChangeEvent) => {
            setLabelAlignment(e.value);
            updatePropertyCentroid('labelAlignment', e.value.value);
            updatePropertyObject('showLabel', e.value.value === DrawingObjectLabelAlignment.perimeter);
        },
        [updatePropertyCentroid, updatePropertyObject]
    );

    const updateLineStyle = React.useCallback((e: DropDownListChangeEvent) => {
        setLineStyle(e.value);
        updatePropertyObject('lineStyle', e.value.value);
    }, []);

    const updateFillColor = React.useCallback(
        (e: ColorPickerChangeEvent) => {
            setFillColor(e.value);
            updatePropertyObject('fillColor', e.value);
        },
        [updatePropertyObject]
    );

    const updateLineColor = React.useCallback(
        (e: ColorPickerChangeEvent) => {
            setLineColor(e.value);
            updatePropertyObject('lineColor', e.value);
        },
        [updatePropertyObject]
    );

    const updateOutlineWidth = React.useCallback(
        (e: SliderChangeEvent) => {
            setLineWidth(Math.round(e.value));
            updatePropertyObject('lineWidth', e.value);
        },
        [updatePropertyObject]
    );

    const updateFillStyle = React.useCallback((e: DropDownListChangeEvent) => {
        setFillStyle(e.value);
        // TODO: props.onPropUpdated("outlineWidth", e.value);
    }, []);

    const updateCenterPointStyle = React.useCallback(
        (e: DropDownListChangeEvent) => {
            setCenterPointStyle(e.value);
            updatePropertyCentroid('centerStyle', e.value.value);
        },
        [updatePropertyCentroid]
    );

    const updateCenterPointColor = React.useCallback(
        (e: ColorPickerChangeEvent) => {
            setCenterPointColor(e.value);
            updatePropertyCentroid('centerColor', e.value);
        },
        [updatePropertyCentroid]
    );

    const updateCenterPointSize = React.useCallback(
        (e: SliderChangeEvent) => {
            setCenterPointSize(Math.round(e.value));
            updatePropertyCentroid('centerSize', e.value);
        },
        [updatePropertyCentroid]
    );

    const updateObjectCategory = React.useCallback((e: DropDownListChangeEvent) => setObjectCategory(e.value), []);

    const updateObjectZoom = React.useCallback(
        (e: SliderChangeEvent) => {
            const zoom = Math.round(e.value);
            setObjectZoom(zoom);
            updatePropertyObject('minZoom', zoom);
        },
        [updatePropertyObject]
    );

    const updatePointZoom = React.useCallback(
        (e: SliderChangeEvent) => {
            const zoom = Math.round(e.value);
            setPointZoom(zoom);
            updatePropertyCentroid('centerMinZoom', zoom);
        },
        [updatePropertyCentroid]
    );

    const updateLabelZoom = React.useCallback(
        (e: SliderChangeEvent) => {
            const zoom = Math.round(e.value);
            setLabelZoom(zoom);
            updatePropertyCentroid('labelMinZoom', zoom);
            updatePropertyObject('labelMinZoom', zoom);
        },
        [updatePropertyCentroid, updatePropertyObject]
    );

    const updateAltitudeMin = React.useCallback((e: NumericTextBoxChangeEvent) => setAltitudeMin(e.value), []);
    const updateAltitudeMax = React.useCallback((e: NumericTextBoxChangeEvent) => setAltitudeMax(e.value), []);

    const updateAlertPlane = React.useCallback((e: CheckboxChangeEvent) => setAlertPlane(e.value), []);
    const updateAlertRoute = React.useCallback((e: CheckboxChangeEvent) => setAlertRoute(e.value), []);

    const updateSyncFPM = React.useCallback((e: CheckboxChangeEvent) => {
        //enable valid dates since they are all required for FPM sync
        if (e.value) {
            setValidTimeStartEnabled(true);
            setValidTimeEndEnabled(true);
            setDailyStartTimeEnabled(true);
            setDailyEndTimeEnabled(true);
        }
        setSyncFPM(e.value);
    }, []);

    const updateObjectFPMSyncType = React.useCallback((e: DropDownListChangeEvent) => setObjectFPMSyncType(e.value.value), []);

    const updateValidTimeStart = React.useCallback(
        (e: DateTimePickerChangeEvent) => setValidTimeStart(DateTimeFormatter.parseTimeFromPicker(e.value)),
        []
    );
    const updateValidTimeEnd = React.useCallback(
        (e: DateTimePickerChangeEvent) => setValidTimeEnd(DateTimeFormatter.parseTimeFromPicker(e.value)),
        []
    );
    const updateValidTimeStartEnabled = React.useCallback((e: SwitchChangeEvent) => setValidTimeStartEnabled(e.value), []);
    const updateValidTimeEndEnabled = React.useCallback((e: SwitchChangeEvent) => setValidTimeEndEnabled(e.value), []);
    const updateDailyStartTime = React.useCallback(
        (e: TimePickerChangeEvent) => setDailyStartTime(DateTimeFormatter.parseTimeFromPicker(e.value, true)),
        []
    );
    const updateDailyEndTime = React.useCallback(
        (e: TimePickerChangeEvent) => setDailyEndTime(DateTimeFormatter.parseTimeFromPicker(e.value, true)),
        []
    );
    const updateDailyStartTimeEnabled = React.useCallback((e: SwitchChangeEvent) => setDailyStartTimeEnabled(e.value), []);
    const updateDailyEndTimeEnabled = React.useCallback((e: SwitchChangeEvent) => setDailyEndTimeEnabled(e.value), []);
    const updateValidDaysOfWeek = React.useCallback((e: ChipListChangeEvent) => {
        setValidDaysOfWeek(e.value.reduce((mask: number, value: number) => mask | value, 0));
    }, []);
    const updateGrayOut = React.useCallback((e: CheckboxChangeEvent) => setGrayOut(e.value), []);

    const convertBitmaskToSelectedDays = (bitmask: number): number[] => {
        return DaysOfWeekOptions.filter((day) => (bitmask & day.value) !== 0).map((day) => day.value);
    };
    const updateOffsetUnits = React.useCallback(
        (e: DropDownListChangeEvent) => {
            setOffsetUnits(e.value);
            const [newX, newY] = convertObjectDimensions(
                [centerX, centerY],
                offsetRadiusX,
                offsetRadiusY,
                e.value.value as DrawingObjectOffsetUnits
            );
            setOffsetRadiusX(newX);
            setOffsetRadiusY(newY);
        },
        [offsetRadiusX, offsetRadiusY, centerX, centerY]
    );

    const updateOffsetRadiusX = React.useCallback((value: number | null) => {
        setOffsetRadiusX(value ?? 0);
    }, []);

    const updateOffsetRadiusY = React.useCallback((value: number | null) => {
        setOffsetRadiusY(value ?? 0);
    }, []);

    const updateCenterX = React.useCallback(
        (value: number | null) => {
            setCenterX(value ?? 0);
            if (props.object.type === DrawingObjectType.polygon) {
                const diff = (value ?? 0) - centerX;
                const newPoints = [...polygonPoints];
                for (const point of newPoints) {
                    point.lngLat[0] += diff;
                }
                setPolygonPoints(newPoints);
            }
        },
        [centerX, polygonPoints]
    );

    const updateCenterY = React.useCallback(
        (value: number | null) => {
            setCenterY(value ?? 0);
            if (props.object.type === DrawingObjectType.polygon) {
                const diff = (value ?? 0) - centerY;
                const newPoints = [...polygonPoints];
                for (const point of newPoints) {
                    point.lngLat[1] += diff;
                }
                setPolygonPoints(newPoints);
            }
        },
        [centerY, polygonPoints]
    );

    // Syncs geometry changes to the drawing layer
    React.useEffect(() => {
        if (props.object.type === DrawingObjectType.polygon && polygonPoints.length < 3) {
            return;
        }
        layer.updateGeometry(props.object.id, props.object.type, getCurrentGeometry());
    }, [polygonPoints, centerX, centerY, centerUnits, offsetRadiusX, offsetRadiusY, offsetUnits, polygonPoints]);

    const handleSetToCurrentZoom = React.useCallback((from: 'object' | 'point' | 'label') => {
        const zoom = map!.getZoom();
        switch (from) {
            case 'object':
                updateObjectZoom({ value: zoom } as SliderChangeEvent);
                return;
            case 'point':
                updatePointZoom({ value: zoom } as SliderChangeEvent);
                return;
            case 'label':
                updateLabelZoom({ value: zoom } as SliderChangeEvent);
                return;
        }
    }, []);

    const getCurrentGeometry = React.useCallback((): DrawingObjectGeometry => {
        const geometry =
            props.object.type === DrawingObjectType.circle
                ? {
                      centerX: centerX,
                      centerY: centerY,
                      centerUnits: centerUnits,
                      radiusX: offsetRadiusX,
                      radiusY: offsetRadiusY,
                      radiusUnits: offsetUnits.value
                  }
                : props.object.type === DrawingObjectType.rectangle
                  ? {
                        centerUnits: centerUnits,
                        centerX: centerX,
                        centerY: centerY,
                        width: offsetRadiusX,
                        height: offsetRadiusY,
                        sizeUnits: offsetUnits.value
                    }
                  : {
                        polygonCoordinates: polygonPoints.map((x) => x.lngLat)
                    };
        return geometry;
    }, [polygonPoints, centerX, centerY, centerUnits, offsetRadiusX, offsetRadiusY, offsetUnits, polygonPoints]);

    const getUpdatedObject = (): DrawingObject => {
        const object: Omit<DrawingObject, 'geometry'> = {
            id: props.object.id,
            name: objectName,
            isCompany: objectType.value,
            type: props.object.type,
            label: {
                text: label,
                alignment: labelAlignment.value,
                color: labelColor
            },
            line: {
                style: lineStyle.value,
                width: lineWidth,
                color: lineColor
            },
            fill: {
                style: fillStyle.value,
                color: fillColor
            },
            center: {
                style: centerPointStyle.value,
                color: centerPointColor,
                size: centerPointSize
            },
            zoom: {
                object: objectZoom,
                point: pointZoom,
                label: labelZoom
            },
            altitude: {
                min: altitudeMin,
                max: altitudeMax
            },
            alerts: {
                plane: alertPlane,
                route: alertRoute
            },
            category: objectCategory.value,
            fpmProperties: {
                sync: syncFPM,
                FPMSyncType: objectFPMSyncType
            },
            validTime: {
                startDay: validTimeStartEnabled ? (validTimeStart?.toISOString() ?? null) : null,
                endDay: validTimeEndEnabled ? (validTimeEnd?.toISOString() ?? null) : null,
                startTime: dailyStartTimeEnabled ? (dailyStartTime ? dayjs(dailyStartTime).utc().format('HH:mm:ss') : null) : null,
                endTime: dailyEndTimeEnabled ? (dailyEndTime ? dayjs(dailyEndTime).utc().format('HH:mm:ss') : null) : null,
                activeDaysOfWeek: validDaysOfWeek,
                greyOutNonActive: grayOut
            }
        };

        // Bit of a hack, but at least the object above is checked for type correctness this way
        (object as DrawingObject).geometry = getCurrentGeometry();
        return object as DrawingObject;
    };

    const setGraphicalEdit = React.useCallback(
        (value: boolean) => {
            setIsEditing(value);
            // Activate
            if (value) {
                layer.graphicEditStart();
            }
            // Save
            else {
                const updatedGeometry = layer.graphicEditApply();
                updateGeometryProperties(props.object.type, updatedGeometry);
            }
        },
        [getCurrentGeometry]
    );

    const cancelGraphicalEdit = React.useCallback(() => {
        setIsEditing(false);
        layer.graphicEditCancel();
    }, []);

    const handlePointChange = React.useCallback(
        (index: number, newPosition: number[]) => {
            const newPositions = [...polygonPoints];
            newPositions[index] = { ...newPositions[index], lngLat: newPosition };
            if (index === 0) {
                newPositions[newPositions.length - 1] = { id: nextId(), lngLat: [...newPosition] };
            }
            setPolygonPoints(newPositions);
        },
        [polygonPoints]
    );

    const handlePointDelete = React.useCallback(
        (index: number) => {
            const newPositions = [...polygonPoints.slice(0, index), ...polygonPoints.slice(index + 1)];
            if (index === 0) {
                newPositions[newPositions.length - 1] = { ...newPositions[0], id: nextId() };
            }
            setPolygonPoints(newPositions);
        },
        [polygonPoints]
    );

    const addPolygonPoint = React.useCallback(() => {
        const last = polygonPoints[polygonPoints.length - 1];
        const newPoints = [
            ...polygonPoints,
            {
                id: nextId(),
                lngLat: [...last.lngLat]
            }
        ];
        setPolygonPoints(newPoints);
    }, [polygonPoints]);

    const handlePastePointsFromClipboard = React.useCallback(async () => {
        setPasteError(false);
        const text = await navigator.clipboard.readText();
        let points: [number, number][] = [];
        try {
            points = parsePositions(text);
        } catch {
            setPasteError(true);
            return;
        }

        if (points.length < 3) {
            setPasteError(false);
            return;
        }

        const first = points[0];
        const last = points[points.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) {
            points.push([...first]);
        }

        setPolygonPoints(
            points.map((x) => ({
                id: nextId(),
                lngLat: [x[1], x[0]]
            }))
        );
    }, [polygonPoints]);

    const handleCopyPointsToClipboard = React.useCallback(async () => {
        const text = polygonPoints.map((x) => formatLatLngNotam(x.lngLat)).join('\n');
        await navigator.clipboard.writeText(text);
    }, [polygonPoints]);

    const handleSave = async () => {
        setBusy(true);
        setErrorMessage('');

        const updatedObject = getUpdatedObject();

        const result = isNew
            ? await dispatch(createDrawingObject(updatedObject)).unwrap()
            : await dispatch(updateDrawingObject(updatedObject)).unwrap();

        setBusy(false);

        if (!result.success) {
            if (result.duplicateName) {
                setErrorMessage('An object with this name already exists. Please choose a different name.');
                nameFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                setErrorMessage('Unknown error saving drawing object.');
            }
            return;
        }

        if (isNew && updatedObject.category === DrawingObjectCategory.viewBased) {
            dispatch(
                patchFocusedWorkspaceView((x) => {
                    // TODO: maybe hide alert areas, and avoidance areas if show was false
                    // Otherwise after adding an new drawing object this flips to and and both alert and avoidance areas are on by default which makes them appear as well as this new object
                    x.staticData.drawingObjects.show = true;
                    x.staticData.drawingObjects.view = true;

                    if (!x.staticData.drawingObjects.viewObjectIds.includes(result.object!.id)) {
                        x.staticData.drawingObjects.viewObjectIds.push(result.object!.id);
                    }
                })
            );
        }
        dispatch(drawingObjectSetIdleWorkspace());
    };

    const handleCancel = React.useCallback(() => {
        dispatch(drawingObjectSetIdleWorkspace());
    }, [props.object]);

    const isPolygon = props.object.type === DrawingObjectType.polygon;
    const isCircle = props.object.type === DrawingObjectType.circle;
    const isRectangle = props.object.type === DrawingObjectType.rectangle;

    const onKeyDown = React.useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                setGraphicalEdit(false);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelGraphicalEdit();
            }
        },
        [setGraphicalEdit, cancelGraphicalEdit]
    );

    React.useEffect(() => {
        if (isEditing) {
            document.addEventListener('keydown', onKeyDown);
            return () => document.removeEventListener('keydown', onKeyDown);
        }
    }, [isEditing, onKeyDown]);

    React.useEffect(() => {
        layer.beginEdit(props.object);
        return () => {
            layer.endEdit();
        };
    }, []);

    return (
        <div
            className="position-relative bg-color border-left border-right h-100 d-flex flex-column pe-auto"
            id="drawing-objects-edit-window"
            style={{ width: 500 }}>
            <div className="p-3 overflow-auto flex-grow-1 d-flex flex-column gap-1">
                {debug && <div className="font-mono fe-badge    align-self-start">Id: {props.object.id}</div>}
                <div>Coordinates</div>
                {/* <div className='pt-2'>
                    <div className='secondary-text'>Center</div>
                    <DropDownList data={["Lat/Long", "Pixel"]} value={"Lat/Long"} ></DropDownList>
                </div> */}

                <div className="secondary-text">Center</div>
                <div>
                    <div className="d-flex gap-2 pt-2 align-items-center">
                        <div>Latitude:</div>
                        <LatLngInput disabled={isEditing} value={centerY} onChange={updateCenterY} type={'lat'} />
                        <div>Longitude:</div>
                        <LatLngInput disabled={isEditing} value={centerX} onChange={updateCenterX} type={'lng'} />
                    </div>
                </div>

                {(isRectangle || isCircle) && (
                    <div className="pt-2">
                        <div className="secondary-text">{isCircle ? 'Radius' : 'Size'}</div>
                        <DropDownList
                            data={OffsetUnitsOptions}
                            textField="text"
                            value={offsetUnits}
                            onChange={updateOffsetUnits}></DropDownList>
                        <div className="d-flex gap-2 pt-2 align-items-center">
                            <div>{isCircle ? 'N/S:' : 'Height:'}</div>

                            {offsetUnits.value === DrawingObjectOffsetUnits.degrees && (
                                <LatLngInput type={'lat'} value={offsetRadiusY} onChange={updateOffsetRadiusY} />
                            )}

                            {offsetUnits.value === DrawingObjectOffsetUnits.miles && (
                                <NumericTextBox format={'#.000000'} value={offsetRadiusY} onChange={(e) => updateOffsetRadiusY(e.value)} />
                            )}

                            <div>{isCircle ? 'E/W:' : 'Width'}</div>

                            {offsetUnits.value === DrawingObjectOffsetUnits.degrees && (
                                <LatLngInput type={'lng'} value={offsetRadiusX} onChange={updateOffsetRadiusX} />
                            )}

                            {offsetUnits.value === DrawingObjectOffsetUnits.miles && (
                                <NumericTextBox format={'#.000000'} value={offsetRadiusX} onChange={(e) => updateOffsetRadiusX(e.value)} />
                            )}
                        </div>
                    </div>
                )}

                <div className="mt-2"></div>
                {!isEditing && (
                    <Button svgIcon={EditOutline} className="w-100" fillMode={'outline'} onClick={() => setGraphicalEdit(true)}>
                        Edit graphically
                    </Button>
                )}
                {isEditing && (
                    <div className="d-flex">
                        <Button fillMode={'solid'} className="w-50 me-1" themeColor={'primary'} onClick={() => setGraphicalEdit(false)}>
                            Save
                        </Button>
                        <Button fillMode={'outline'} className="w-50 ms-1" onClick={cancelGraphicalEdit}>
                            Cancel
                        </Button>
                    </div>
                )}
                {isPolygon && (
                    <>
                        <DrawingObjectPolygonPoints
                            disabled={isEditing}
                            points={polygonPoints}
                            onPointDelete={handlePointDelete}
                            onPointChange={handlePointChange}
                        />
                        {pasteError && <div className="secondary-text text-danger text-center">Could not read points from clipboard</div>}
                        <Button svgIcon={AddOutline} fillMode={'outline'} onClick={addPolygonPoint} disabled={isEditing}>
                            Add point
                        </Button>
                        <Button
                            disabled={isEditing}
                            className="w-100 mt-2"
                            svgIcon={clipboardCodeIcon}
                            fillMode={'outline'}
                            onClick={handlePastePointsFromClipboard}>
                            Paste points from clipboard
                        </Button>
                        <Button className="w-100 mt-2" svgIcon={copyIcon} fillMode={'outline'} onClick={handleCopyPointsToClipboard}>
                            Copy points to clipboard
                        </Button>
                    </>
                )}

                <div>Object type: {DrawingObjectType[props.object.type]}</div>

                <div>
                    <div ref={nameFieldRef} className="secondary-text">
                        Object name
                    </div>
                    <TextBox placeholder={'Name'} value={objectName} onChange={updateObjectName} />
                    {errorMessage && (
                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                            {errorMessage}
                        </div>
                    )}
                </div>

                {rights.companyFeaturesEnabled && (
                    <div>
                        <div className="secondary-text">Type</div>
                        <DropDownList
                            disabled={!isNew || !rights.canEditCompanyViews}
                            value={objectType}
                            data={ObjectType}
                            valueRender={() => (
                                <div
                                    className="w-100 d-flex align-items-center px-2 gap-1"
                                    style={{ paddingTop: '6px', paddingBottom: '6px' }}>
                                    <SvgIcon icon={objectType.value ? UserGroupOutline : UserOutline} />
                                    <span>{objectType.text}</span>
                                </div>
                            )}
                            textField="text"
                            dataItemKey="value"
                            onChange={updateObjectType}></DropDownList>
                    </div>
                )}

                <div>
                    <div className="secondary-text">Label (on-screen)</div>
                    <div className="d-flex gap-2">
                        <TextBox placeholder={'Label'} value={label} onChange={updateLabel} />
                        <div style={{ width: '130px' }} className="flex-shrink-0">
                            <DropDownList
                                data={LabelAlignmentOption}
                                textField="text"
                                value={labelAlignment}
                                onChange={updateLabelAlignment}
                            />
                        </div>
                        <div>
                            <ColorPickerWithDefault
                                defaultColor={DRAWING_OBJECT_LABEL_DEFAULT_COLOR}
                                views={['palette', 'gradient']}
                                value={labelColor}
                                onChange={updateLabelColor}
                            />
                        </div>
                    </div>
                    {/* <div className='pt-2'>
                        <div className='secondary-text pt-2'>Label offset</div>
                        <div className='d-flex gap-2 align-items-center'>
                            <DropDownList data={["Miles", "Pixels"]} value={"Miles"} />
                            X:
                            <NumericTextBox value={-5} width={120} className='flex-shrink-0' />
                            Y:
                            <NumericTextBox value={5} width={120} className='flex-shrink-0' />
                        </div>
                    </div> */}
                </div>

                <div>
                    <div className="secondary-text">Line settings</div>
                    <div className="d-flex gap-2">
                        <DropDownList
                            data={LineStyleOptions}
                            textField="text"
                            value={lineStyle}
                            dataItemKey="value"
                            onChange={updateLineStyle}
                        />
                        <div>
                            <ColorPickerWithDefault
                                defaultColor={DRAWING_OBJECT_LINE_DEFAULT_COLOR}
                                views={['palette', 'gradient']}
                                value={lineColor}
                                onChange={updateLineColor}
                            />
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 pt-2">
                        <div className="flex-shrink-0">
                            Width: <strong>{lineWidth}</strong>
                        </div>
                        <Slider className="flex-grow-1" min={0} max={10} step={1} value={lineWidth} onChange={updateOutlineWidth} />
                    </div>
                </div>

                <div>
                    <div className="secondary-text">Fill settings</div>
                    <div className="d-flex gap-2">
                        <DropDownList
                            disabled={true}
                            textField="text"
                            data={FillStyleOptions}
                            value={fillStyle}
                            onChange={updateFillStyle}></DropDownList>
                        <div>
                            <ColorPickerWithDefault
                                defaultColor={DRAWING_OBJECT_FILL_DEFAULT_COLOR}
                                views={['palette', 'gradient']}
                                value={fillColor}
                                onChange={updateFillColor}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <div className="secondary-text">Center / vertex point(s)</div>
                    <div className="d-flex gap-2">
                        <DropDownList
                            textField="text"
                            data={CenterStyleOptions}
                            value={centerPointStyle}
                            onChange={updateCenterPointStyle}></DropDownList>
                        <ColorPickerWithDefault
                            defaultColor={DRAWING_OBJECT_CENTER_DEFAULT_COLOR}
                            views={['palette', 'gradient']}
                            value={centerPointColor}
                            onChange={updateCenterPointColor}
                        />
                    </div>
                    <div className="d-flex gap-2 pt-2 align-items-center">
                        <div className="flex-shrink-0">
                            Size: <strong>{centerPointSize}</strong>
                        </div>
                        <Slider className="w-100" min={0} max={10} step={1} value={centerPointSize} onChange={updateCenterPointSize} />
                    </div>
                </div>

                <div>
                    <div className="secondary-text">Category</div>
                    <DropDownList
                        data={ObjectCategoryOptions}
                        value={objectCategory}
                        textField="text"
                        disabled={syncFPM}
                        onChange={updateObjectCategory}></DropDownList>
                </div>

                <div className="secondary-text">Zoom levels</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr max-content', rowGap: '.5rem', columnGap: '.5rem' }}>
                    <div style={{ width: 60 }}>Object</div>
                    <Slider min={0} max={22} className="w-100" value={objectZoom} onChange={updateObjectZoom} />
                    <Button className="button-small" fillMode={'outline'} onClick={() => handleSetToCurrentZoom('object')}>
                        @
                    </Button>

                    <div>Point</div>
                    <Slider min={0} max={22} className="w-100" value={pointZoom} onChange={updatePointZoom} />
                    <Button className="button-small" fillMode={'outline'} onClick={() => handleSetToCurrentZoom('point')}>
                        @
                    </Button>

                    <div>Label</div>
                    <Slider min={0} max={22} className="w-100" value={labelZoom} onChange={updateLabelZoom} />
                    <Button className="button-small" fillMode={'outline'} onClick={() => handleSetToCurrentZoom('label')}>
                        @
                    </Button>
                </div>

                <div className="secondary-text">Altitude boundaries (a blank entry indicates unbounded)</div>
                <div className="d-flex gap-2 align-items-center justify-content-between">
                    <div>From:</div>
                    <NumericTextBox
                        value={altitudeMin}
                        onChange={updateAltitudeMin}
                        width={140}
                        className="flex-shrink-0"
                        format={'# ft'}
                    />
                    <div className="flex-grow-1"></div>
                    <div>to:</div>
                    <NumericTextBox
                        value={altitudeMax}
                        onChange={updateAltitudeMax}
                        width={140}
                        className="flex-shrink-0"
                        format={'# ft'}
                    />
                </div>
                <div className="secondary-text"></div>

                <div className="secondary-text">Alerts</div>
                <Checkbox
                    label="Generate an alert when a plane enters this object's area."
                    value={alertPlane}
                    onChange={updateAlertPlane}></Checkbox>
                <Checkbox
                    label="Generate an alert when a My Fleet plane's route changes and will intersect this object's area."
                    value={alertRoute}
                    disabled={props.object.type !== DrawingObjectType.polygon || !rights.routeAnalytics}
                    onChange={updateAlertRoute}></Checkbox>

                <div className="secondary-text">Start date & time</div>
                <div className="d-flex align-items-center gap-3">
                    <Switch
                        onLabel={''}
                        offLabel={''}
                        onChange={updateValidTimeStartEnabled}
                        checked={validTimeStartEnabled}
                        disabled={syncFPM}
                    />
                    <DateTimePicker
                        value={DateTimeFormatter.formatTimeForPicker(validTimeStart)}
                        disabled={!validTimeStartEnabled}
                        onChange={updateValidTimeStart}
                        format={DateTimeFormatter.getKendoDateTimeFormat()}
                    />
                </div>
                <div className="secondary-text">End date & time</div>
                <div className="d-flex align-items-center gap-3">
                    <Switch
                        onLabel={''}
                        offLabel={''}
                        onChange={updateValidTimeEndEnabled}
                        checked={validTimeEndEnabled}
                        disabled={syncFPM}
                    />
                    <DateTimePicker
                        value={DateTimeFormatter.formatTimeForPicker(validTimeEnd)}
                        disabled={!validTimeEndEnabled}
                        onChange={updateValidTimeEnd}
                        format={DateTimeFormatter.getKendoDateTimeFormat()}
                    />
                </div>
                <div className="secondary-text">Daily start time (UTC)</div>
                <div className="d-flex align-items-center gap-3">
                    <Switch
                        onLabel={''}
                        offLabel={''}
                        onChange={updateDailyStartTimeEnabled}
                        checked={dailyStartTimeEnabled}
                        disabled={syncFPM}
                    />
                    <TimePicker
                        value={DateTimeFormatter.formatTimeForPicker(dailyStartTime, true)}
                        disabled={!dailyStartTimeEnabled}
                        onChange={updateDailyStartTime}
                        format={DateTimeFormatter.getKendoTimeWithoutSecondsFormat()}
                    />
                </div>
                <div className="secondary-text">Daily end time (UTC)</div>
                <div className="d-flex align-items-center gap-3">
                    <Switch
                        onLabel={''}
                        offLabel={''}
                        onChange={updateDailyEndTimeEnabled}
                        checked={dailyEndTimeEnabled}
                        disabled={syncFPM}
                    />
                    <TimePicker
                        value={DateTimeFormatter.formatTimeForPicker(dailyEndTime, true)}
                        disabled={!dailyEndTimeEnabled}
                        onChange={updateDailyEndTime}
                        format={DateTimeFormatter.getKendoTimeWithoutSecondsFormat()}
                    />
                </div>
                <div className="secondary-text">Days active</div>
                <ChipList
                    selection={'multiple'}
                    defaultData={DaysOfWeekOptions}
                    onChange={updateValidDaysOfWeek}
                    value={convertBitmaskToSelectedDays(validDaysOfWeek)}></ChipList>
                <Checkbox label="Gray out on map when not active" value={grayOut} onChange={updateGrayOut} />

                {objectCategory.value === DrawingObjectCategory.avoidance &&
                    !objectType.value && //!isCompany
                    integrationInfo.enabled &&
                    integrationInfo.isFPM && (
                        <div className="mt-2" title={integrationInfo.getDisabledReason('canSendDrawObjToFPM') ?? undefined}>
                            <div className="secondary-text">FPM integration</div>
                            <Checkbox
                                label="Sync with FPM"
                                value={syncFPM}
                                disabled={!integrationInfo.isActionEnabled('canSendDrawObjToFPM')}
                                onChange={updateSyncFPM}
                            />
                            {syncFPM && (
                                <DropDownList
                                    data={ObjectFPMSyncOptions}
                                    value={ObjectFPMSyncOptions.find((x) => x.value === objectFPMSyncType)}
                                    textField="text"
                                    disabled={
                                        !integrationInfo.isActionEnabled('canSendDrawObjToFPM') ||
                                        !integrationInfo.isActionEnabled('canSendUserDrawObjToFPM')
                                    }
                                    onChange={updateObjectFPMSyncType}></DropDownList>
                            )}
                        </div>
                    )}
            </div>

            <div className="d-flex w-100 gap-2 border-top p-2">
                <Button className="flex-grow-1" themeColor={'primary'} onClick={handleSave} disabled={!objectName || busy}>
                    <span>Save</span>
                </Button>
                <Button className="flex-grow-1" fillMode={'outline'} onClick={handleCancel}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

const ObjectType: DropdownItem<boolean>[] = [
    { text: 'Company', value: true },
    { text: 'User', value: false }
];

const LabelAlignmentOption: DropdownItem<DrawingObjectLabelAlignment>[] = [
    { text: 'Center', value: DrawingObjectLabelAlignment.center },
    { text: 'Left', value: DrawingObjectLabelAlignment.left },
    { text: 'Right', value: DrawingObjectLabelAlignment.right },
    { text: 'Perimeter', value: DrawingObjectLabelAlignment.perimeter }
];

const LineStyleOptions: DropdownItem<DrawingObjectLineType>[] = [
    { text: 'Solid', value: DrawingObjectLineType.solid },
    { text: 'Dash', value: DrawingObjectLineType.dash },
    { text: 'Dotted', value: DrawingObjectLineType.dotted },
    { text: 'None', value: DrawingObjectLineType.none }
];

const FillStyleOptions: DropdownItem<DrawingObjectFillStyle>[] = [
    { text: 'None', value: DrawingObjectFillStyle.none },
    { text: 'Solid', value: DrawingObjectFillStyle.solid },
    { text: 'Vertical hatch', value: DrawingObjectFillStyle.verticalHatch },
    { text: 'Horizontal hatch', value: DrawingObjectFillStyle.horizontalHatch },
    { text: 'Cross hatch', value: DrawingObjectFillStyle.crossHatch },
    { text: 'Diag hatch left', value: DrawingObjectFillStyle.diagHatchLeft },
    { text: 'Diag hatch right', value: DrawingObjectFillStyle.diagHatchRight },
    { text: 'Diag cross hatch', value: DrawingObjectFillStyle.diagCrossHatch }
];

const CenterStyleOptions: DropdownItem<DrawingObjectCenterStyle>[] = [
    { text: 'None', value: DrawingObjectCenterStyle.none },
    { text: 'Dot', value: DrawingObjectCenterStyle.dot },
    { text: 'Plus', value: DrawingObjectCenterStyle.plus },
    { text: 'X', value: DrawingObjectCenterStyle.x },
    { text: 'Hollow circle', value: DrawingObjectCenterStyle.hollowCircle },
    { text: 'Filled circle', value: DrawingObjectCenterStyle.filledCircle }
];

const ObjectCategoryOptions: DropdownItem<DrawingObjectCategory>[] = [
    { text: 'View based', value: DrawingObjectCategory.viewBased },
    { text: 'Alert based', value: DrawingObjectCategory.alertBased },
    { text: 'Avoidance area', value: DrawingObjectCategory.avoidance }
];

const ObjectFPMSyncOptions: DropdownItem<FPMSyncType>[] = [
    { text: 'Global', value: FPMSyncType.global },
    { text: 'User Specific', value: FPMSyncType.userSpecific }
];

const OffsetUnitsOptions: DropdownItem<DrawingObjectOffsetUnits>[] = [
    { text: 'Miles', value: DrawingObjectOffsetUnits.miles },
    { text: 'Degrees', value: DrawingObjectOffsetUnits.degrees }
];

const DaysOfWeekOptions: DropdownItem<number>[] = [
    { text: 'Mon', value: 1 << 1 },
    { text: 'Tue', value: 1 << 2 },
    { text: 'Wed', value: 1 << 3 },
    { text: 'Thu', value: 1 << 4 },
    { text: 'Fri', value: 1 << 5 },
    { text: 'Sat', value: 1 << 6 },
    { text: 'Sun', value: 1 << 0 }
];

function val<T>(items: DropdownItem<T>[], value: T): DropdownItem<T> {
    return items.find((x) => x.value === value)!;
}

function convertObjectDimensions(
    center: [number, number],
    width: number,
    height: number,
    target: DrawingObjectOffsetUnits
): [number, number] {
    if (target === DrawingObjectOffsetUnits.miles) {
        // Original are degrees then
        return [
            turf.rhumbDistance(center, [center[0] + width, center[1]], { units: 'nauticalmiles' }),
            turf.rhumbDistance(center, [center[0], center[1] + height], { units: 'nauticalmiles' })
        ];
    }

    // Original miles
    return [
        Math.abs(center[0] - turf.rhumbDestination(center, width, 90, { units: 'nauticalmiles' }).geometry.coordinates[0]),
        Math.abs(center[1] - turf.rhumbDestination(center, height, 180, { units: 'nauticalmiles' }).geometry.coordinates[1])
    ];
}

export default DrawingObjectEditWindow;
